<script lang="ts" setup>
import PageHeader from '@/components/global/page-header/PageHeader.vue'
import ProductSpecificationForm from '@/components/features/productSpecification/ProductSpecificationForm.vue'
import { useProductSpecificationForm } from '@/composables/useProductSpecificationForm'
import { useRouter } from 'vue-router'
import { addProduct } from '@/api/products'
import type { ErrorResponse } from '@/types/error-types'
import { onMounted} from 'vue'
import { Unit } from '@/constants/dimension'
import {FabricStatusEnum} from "@/types/product-types";

const { values, handleSubmit, setErrors, showToast, formType, loading, setFieldValue } =
  useProductSpecificationForm('add')
const router = useRouter()

const onSubmit = handleSubmit( async () => {
  if (values.fabricStatus === FabricStatusEnum.Finished) {
    if (!values.fabricYieldValue || isNaN(Number(values.fabricYieldValue)) || Number(values.fabricYieldValue) <= 0) {
      showToast('error', 'Validation Error', 'Fabric yield is required for finished products.')
      return
    }
  }
  loading.value = true

  const formData = {
    finishedReference: values.finishedReference || '',
    unfinishedReference: values.unfinishedReference || '',
    alternateReference: values.alternateReference || '',
    displayedReference: values.displayedReference || '',
    colour: values.colour || '',
    finish: values.finish || '',
    fabricWidthValue: values.fabricWidthValue || 0,
    fabricWidthUnit: values.fabricWidthUnit || 0,
    fabricYieldValue: values.fabricYieldValue || 0,
    fabricYieldUnit: values.fabricYieldUnit || 0,
    averageRollLengthValue: values.averageRollLengthValue || 0,
    averageRollLengthUnit: values.averageRollLengthUnit || 0,
    construction: values.construction || '',
    fabricWeight: values.fabricWeight || 0,
    fabricStatus: values.fabricStatus || 0,
    standardBatchSize: values.standardBatchSize,
    guage: values.guage || '',
    fibreContent: values.fibreContent || '',
    productCosts: values.productCosts || [],
    merges: values.merges || [],
    yarnsUsed: values.yarnsUsed || []
  }

  try {
    await addProduct(formData)
    loading.value = false
    showToast('success', 'Product Added!', 'You have successfully added a new product.')
    await router.push('/productSpecification')
  } catch (error: any) {
    loading.value = false

    if (error.response?.data?.errors) {
      const errorMessages = error.response.data.errors
        .map((err: { code: string; description: string }) => err.description)
        .join('\n')
      showToast('error', 'Validation Error', errorMessages)
    }
    else if (error.response) {
      showToast('error', 'Error', `Server Error: ${error.response.status} - ${error.response.data.title || error.message}`)
    }
    else if (error.request) {
      showToast('error', 'Error', 'No response from the server.')
    }
    else {
      showToast('error', 'Error', `Request setup error: ${error.message}`)
    }
  }
},
  (errors: Record<string, string>) => {
  loading.value = false

  const errorObjects = Object.entries(errors.errors).map(([field, message]) => ({
    code: field,
    description: message,
  }))

  if(errorObjects.length > 0) {
    const firstError = errorObjects[0]
    const errorMessage = ` ${firstError.description}`
    showToast('error', 'Validation Error', errorMessage)
    return
  }
}
)

onMounted(() => {
  const defaultFabricWidthUnit = Unit.CentiMetres
  const defaultFabricYieldUnit = Unit.Metres
  const defaultAverageRollLengthUnit = Unit.Metres
  setFieldValue('fabricWidthUnit', defaultFabricWidthUnit)
  setFieldValue('fabricYieldUnit', defaultFabricYieldUnit)
  setFieldValue('averageRollLengthUnit', defaultAverageRollLengthUnit)
})
</script>

<template>
  <PageHeader
    title="Add a Product"
    buttonText="Save Product"
    buttonIcon="material-symbols:save"
    :buttonClick="onSubmit"
    :loading="loading"
  />

  <div class="bg-white rounded-xl pb-2">
    <ProductSpecificationForm
      :type="formType"
      :values="values"
      :onSubmit="onSubmit"
      :setErrors="setErrors"
    />
  </div>
</template>
